import {
  ActionIcon,
  Affix,
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Transition,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';
import { useState, type ReactNode } from 'react';
import { useIsPhone } from '../../hooks/useIsPhone';

/** A single action button rendered inside the {@link BulkActionBar}. */
export interface BulkAction {
  /** Visible button label. */
  label: string;
  /** Optional left-section icon. */
  icon?: ReactNode;
  /** The async operation to run. Receives the array of selected IDs. */
  onAction: (ids: string[]) => Promise<void>;
  /** Optional Mantine color (e.g. `'red'` for destructive actions). */
  color?: string;
  /**
   * If provided, a Mantine <Modal> confirmation dialog is shown before
   * running `onAction`. The string is the body copy of the confirm modal.
   */
  confirmMessage?: string;
}

/** Props for {@link BulkActionBar}. */
export interface BulkActionBarProps {
  /** Number of currently selected rows. Bar is hidden when 0. */
  selectedCount: number;
  /** Ordered array of the currently selected IDs — passed to onAction. */
  selectedIds: string[];
  /** Buttons rendered on the right side of the bar. */
  actions: BulkAction[];
  /** Called when the close (×) affordance is clicked. */
  onClear: () => void;
  /**
   * Optional caller-composed summary rendered between the "N selected" count
   * and the action buttons — e.g. bins' "N graded · N no barcode · $N
   * sticker total" (vendor#408), stated up front in the terms the next
   * action needs rather than making the dealer guess which destination will
   * refuse the selection. Omitted by every other consumer of this shared bar
   * (inventory, binders, sales, the bins list, the design-system page) —
   * backward compatible, opt-in.
   */
  summary?: ReactNode;
}

/**
 * BulkActionBar — bottom-pinned floating bar shown when one or more table
 * rows are selected.
 *
 * Shell: any (desktop / mobile).
 *
 * Renders inside Mantine's `<Affix>` near the bottom-center with a `<Paper>`
 * shell (shadow=md, primary-light background). Returns `null` when
 * `selectedCount === 0` so the surface is fully absent from the DOM when not
 * needed. Wrapped in a `<Transition>` for a subtle slide-up on appearance.
 *
 * Non-obvious contracts:
 *  - Each action receives the current `selectedIds` array when invoked.
 *  - Actions with `confirmMessage` open a Mantine `<Modal>` first; the
 *    action runs only on Confirm.
 *  - While an action is in flight, its button shows a loading spinner and
 *    the other action buttons are disabled (single-flight per bar).
 *  - On `onAction` rejection a red Mantine notification is shown; the bar
 *    stays mounted so the user can retry.
 *  - `summary` is optional, caller-composed, dimmed text between the count
 *    and the actions — undefined for every consumer that doesn't pass it.
 *  - **Phone layout (vendor#408).** At `useIsPhone()`, the action row
 *    becomes equal-width, stacked icon-over-label tap targets
 *    (`var(--tap-min)` tall, wrapping into further rows past the first) —
 *    the design's phone bulk-bar pattern — instead of the desktop's small
 *    inline `<Button>` row, which is unusable at thumb width. The
 *    Affix/Paper shell and every other prop/behavior (confirm modal,
 *    single-flight loading, `summary`) are unchanged and shared between
 *    both layouts; only the actions' internal markup forks. The design's
 *    own phone bar has exactly 4 acts (its own action set); this repo's
 *    bins bar can pass 5 (Export selected/Delete lines were promoted from
 *    desktop-kebab-only to first-class actions in an earlier pass) — every
 *    action passed renders, rather than truncating to a fixed count and
 *    silently dropping a capability the design's phone treatment predates.
 */
export function BulkActionBar({
  selectedCount,
  selectedIds,
  actions,
  onClear,
  summary,
}: BulkActionBarProps) {
  const isPhone = useIsPhone();
  const visible = selectedCount > 0;
  const [loading, setLoading] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<BulkAction | null>(null);
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);

  if (!visible) return null;

  async function runAction(action: BulkAction) {
    setLoading(action.label);
    try {
      await action.onAction(selectedIds);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Action failed',
        message: err instanceof Error ? err.message : 'An unexpected error occurred.',
      });
    } finally {
      setLoading(null);
    }
  }

  function handleClick(action: BulkAction) {
    if (action.confirmMessage) {
      setPendingAction(action);
      openConfirm();
      return;
    }
    void runAction(action);
  }

  async function handleConfirm() {
    const action = pendingAction;
    closeConfirm();
    setPendingAction(null);
    if (action) {
      await runAction(action);
    }
  }

  function handleCancel() {
    closeConfirm();
    setPendingAction(null);
  }

  const countRow = (
    <Group gap="xs" wrap="nowrap">
      <Text fw={600} size="sm">
        {selectedCount} selected
      </Text>
      <ActionIcon variant="subtle" size="sm" onClick={onClear} aria-label="Clear selection">
        <IconX size={16} />
      </ActionIcon>
    </Group>
  );

  const summaryText = summary != null && (
    <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
      {summary}
    </Text>
  );

  const body = isPhone ? (
    <Stack gap={8}>
      {countRow}
      {summaryText}
      <Group gap={6} wrap="wrap">
        {actions.map((a) => {
          const isThisLoading = loading === a.label;
          const disabled = loading !== null && !isThisLoading;
          return (
            <UnstyledButton
              key={a.label}
              onClick={() => handleClick(a)}
              disabled={disabled}
              style={{
                flex: '1 1 76px',
                minHeight: 'var(--tap-min, 44px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                padding: '6px 4px',
                borderRadius: 'var(--mantine-radius-sm)',
                border: '1px solid var(--mantine-color-default-border)',
                color: a.color ? `var(--mantine-color-${a.color}-6)` : undefined,
                opacity: disabled ? 0.5 : 1,
              }}
            >
              {a.icon}
              <Text fz={9.5} fw={600} c="inherit" ta="center" style={{ lineHeight: 1.1 }}>
                {a.label}
              </Text>
            </UnstyledButton>
          );
        })}
      </Group>
    </Stack>
  ) : (
    <Group gap="md" wrap="nowrap">
      {countRow}
      {summaryText}
      <Group gap="xs" wrap="nowrap">
        {actions.map((a) => (
          <Button
            key={a.label}
            size="xs"
            color={a.color}
            leftSection={a.icon}
            onClick={() => handleClick(a)}
            loading={loading === a.label}
            disabled={loading !== null && loading !== a.label}
          >
            {a.label}
          </Button>
        ))}
      </Group>
    </Group>
  );

  return (
    <>
      <Affix position={{ bottom: 16, left: '50%' }} style={{ transform: 'translateX(-50%)' }}>
        <Transition mounted={visible} transition="slide-up" duration={150}>
          {(styles) => (
            <Paper
              withBorder
              shadow="md"
              radius="md"
              p="sm"
              style={{
                ...styles,
                backgroundColor: 'var(--mantine-color-brand-0)',
                zIndex: 200,
                maxWidth: isPhone ? 'calc(100vw - 32px)' : undefined,
              }}
            >
              {body}
            </Paper>
          )}
        </Transition>
      </Affix>
      <Modal
        opened={confirmOpened}
        onClose={handleCancel}
        title={pendingAction?.label ?? ''}
        centered
      >
        <Text size="sm" mb="md">
          {pendingAction?.confirmMessage}
        </Text>
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={handleCancel}>
            Cancel
          </Button>
          <Button color={pendingAction?.color} onClick={() => void handleConfirm()}>
            Confirm
          </Button>
        </Group>
      </Modal>
    </>
  );
}
